/**
 * Query parameters that may be carried into telemetry.
 *
 * This is an allowlist, not a denylist, so it fails closed: a parameter added
 * to the app next year is dropped from telemetry until someone deliberately
 * adds it here. That matters because our URLs carry capabilities and personal
 * data — `sessionId` is a share link that ADOPTS a booking session (see
 * `generate-share-booking-link.ts`), `custid` identifies a customer, and
 * `hotelComment` is free text the guest typed.
 *
 * What stays is the booking shape, which is what makes an error reproducible.
 */
const ALLOWED_QUERY_PARAMS = new Set([
  'arrival',
  'nights',
  'adults',
  'room1',
  'room2',
  'room3',
  'roomCategory',
  'offering',
  'language',
]);

/**
 * Path segments are capabilities too, not just query parameters.
 *
 * `/add-variant/<id>` adds a private offer to the guest's session
 * (`add-variant.loader.ts`), so the identifier in the path is as sensitive as
 * `?sessionId=` is in the query. Masking to the route shape keeps the path
 * useful for grouping while the identifier never reaches the log store.
 *
 * `paths.ts` declares exactly one dynamic route today — extend this list
 * alongside it. The UUID rule is the general catch-all: it also bounds
 * attribute cardinality, since an un-normalised id makes every request URL a
 * distinct attribute value.
 */
const PATH_REDACTIONS: Array<[RegExp, string]> = [
  // The trailing slash is absorbed so `/add-variant/x` and `/add-variant/x/`
  // group as one value rather than two.
  [/^\/add-variant\/[^/]+\/?/i, '/add-variant/:variantId'],
  [
    /\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi,
    '/:uuid',
  ],
];

// A single attribute should never be able to dominate a batch. Query values are
// guest-controlled, so without a cap an oversized `?offering=…` would ride into
// every record on the page.
const MAX_URL_LENGTH = 512;

// Only used to parse relative URLs; never emitted.
const PARSE_BASE = 'https://redacted.invalid';

const ABSOLUTE_URL = /^[a-z][a-z0-9+.-]*:\/\//i;

const redactPath = (pathname: string): string =>
  PATH_REDACTIONS.reduce(
    (path, [pattern, replacement]) => path.replace(pattern, replacement),
    pathname,
  );

/**
 * Strips every query parameter outside the allowlist, masks identifiers in the
 * path, and drops the fragment. Accepts absolute and relative URLs; returns the
 * same shape it was given.
 */
export const redactUrl = (raw: string | undefined | null): string => {
  if (!raw) return '';

  try {
    const isAbsolute = ABSOLUTE_URL.test(raw);
    const url = new URL(raw, PARSE_BASE);

    const kept = new URLSearchParams();
    url.searchParams.forEach((value, key) => {
      if (ALLOWED_QUERY_PARAMS.has(key)) kept.append(key, value);
    });

    const pathname = redactPath(url.pathname);
    const query = kept.toString();
    const path = query ? `${pathname}?${query}` : pathname;

    return (isAbsolute ? `${url.origin}${path}` : path).slice(
      0,
      MAX_URL_LENGTH,
    );
  } catch {
    // An unparseable URL is dropped rather than passed through — passing it
    // through is exactly the leak this function exists to prevent.
    return '';
  }
};
